import type { Band } from '../../lib/format';
import type { OnboardingCategory, OnboardingTask, OnboardingTaskStatus } from '../../lib/types';

/**
 * The onboarding tab's vocabulary, in one place.
 *
 * The source app coloured its seven categories with seven hues — blue, purple,
 * red, amber, green, grey. This brand has two colours and neither green nor
 * amber is one of them, so category is carried by a glyph and a label instead,
 * and colour is left to say the one thing it is reserved for here: how bad
 * something is. See the note at the top of components/data.tsx.
 */

export const CATEGORIES: OnboardingCategory[] = [
  'communication',
  'onboarding',
  'documentation',
  'setup',
  'security',
  'internal',
  'other',
];

export const CATEGORY_LABEL: Record<OnboardingCategory, string> = {
  communication: 'Communication',
  onboarding: 'Onboarding',
  documentation: 'Documentation',
  setup: 'Setup',
  security: 'Security',
  internal: 'Internal only',
  other: 'Other',
};

export const CATEGORY_GLYPH: Record<OnboardingCategory, string> = {
  communication: '✉',
  onboarding: '§',
  documentation: '▤',
  setup: '⚙',
  security: '⛨',
  internal: '⌂',
  other: '·',
};

export const TASK_STATUS_LABEL: Record<OnboardingTaskStatus, string> = {
  pending: 'To do',
  in_progress: 'In progress',
  complete: 'Done',
};

export const COMPANY_SIZES = [
  { value: '1-10', label: '1–10 employees' },
  { value: '11-50', label: '11–50 employees' },
  { value: '51-200', label: '51–200 employees' },
  { value: '201-500', label: '201–500 employees' },
  { value: '500+', label: '500+ employees' },
];

export const TIMELINES = [
  { value: 'asap', label: 'ASAP — within two weeks' },
  { value: '1month', label: 'Within a month' },
  { value: '3months', label: 'Within three months' },
  { value: 'evaluating', label: 'Still evaluating' },
];

export const TIMELINE_LABEL: Record<string, string> = Object.fromEntries(
  TIMELINES.map((t) => [t.value, t.label]),
);

export const BACKUP_TYPES = [
  { value: 'cloud', label: 'Cloud backup', hint: 'Backed up to cloud storage — Azure, AWS, Backblaze.' },
  { value: 'server', label: 'Server / on-premises', hint: 'Backed up to a local or on-site server.' },
  { value: 'both', label: 'Both', hint: 'Hybrid: cloud and on-premises together.' },
];

export const INDUSTRIES = [
  'Healthcare',
  'Finance & Banking',
  'Legal',
  'Manufacturing',
  'Retail & eCommerce',
  'Education',
  'Non-Profit',
  'Real Estate',
  'Construction',
  'Technology',
  'Other',
];

/**
 * How urgent a task is, on this app's badge ladder.
 *
 * Overdue is the only thing that climbs the crimson ramp. Everything else is
 * neutral or done, because a task with three days left is not a warning — it
 * is a task, and treating it as one makes the genuinely late ones invisible.
 */
export function taskBand(task: OnboardingTask, today: string): Band {
  if (task.Status === 'complete') return 'good';
  if (task.DueDate && task.DueDate.slice(0, 10) < today) return 'critical';
  if (task.DueDate && task.DueDate.slice(0, 10) === today) return 'warning';
  return 'neutral';
}

/** Progress as a whole percentage, and 0 rather than NaN with nothing assigned. */
export function progressPct(complete: number, total: number): number {
  return total > 0 ? Math.round((complete / total) * 100) : 0;
}

/**
 * Fills the three placeholders the source app's templates used.
 *
 * Kept exactly as they were — `{{Client Name}}`, `{firstName}`, `{companyName}`
 * — so an email body pasted out of the old library still works here.
 */
export function fillPlaceholders(
  text: string,
  values: { firstName: string; companyName: string },
): string {
  const person = values.firstName || values.companyName || 'there';
  return text
    .replace(/\{\{Client Name\}\}/gi, person)
    .replace(/\{firstName\}/g, person)
    .replace(/\{companyName\}/g, values.companyName);
}

/**
 * Opens the composer in whatever the person uses for mail.
 *
 * A `mailto:` rather than sending from the server, deliberately. These are
 * one-to-one messages from a named human at the start of a relationship, they
 * need to be edited before they go, and the reply has to land in that person's
 * inbox — none of which is true of the CSAT mail this app does send itself.
 */
export function mailtoHref(to: string, subject: string, body: string): string {
  const params = new URLSearchParams({ subject, body });
  return `mailto:${encodeURIComponent(to)}?${params.toString().replace(/\+/g, '%20')}`;
}

/** File size, for a list of attachments. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
